import { useApolloClient } from '@apollo/client';
import { useFormContext } from 'contexts/FormContext';
import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { ClockHistory, Dash, Plus } from 'react-bootstrap-icons';
import CurrencyFormat from 'react-currency-format';
import historygql from 'gql/dashboard/sales/history.gql';
import { toast } from 'react-toastify';
import DefaultCurrency from 'components/DefaultCurrency';
import { useAppContext } from 'contexts/AppContext';
import ItemBatchSummary from './ItemBatchSummary';
import DateFormat from 'components/DateFormat';

const NormalView = ({ item }) => {
  // contexts
  const {
    formState,
    setFormState,
    addDetail,
    bulkAdjustDetails,
    removeDetail,
  } = useFormContext();
  const { appState } = useAppContext();

  // states
  const [price, setPrice] = useState();
  const [qty, setQty] = useState(0);
  const [waitingForCustomer, setWaitingForCustomer] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  // hooks
  const apollo = useApolloClient();

  // functions
  const add = (e) => {
    e.preventDefault();
    e.stopPropagation();

    addDetail({
      item,
      price,
      qty,
    });
  };

  const fetchHistory = () => {
    if (!formState.info.customer?.value) {
      setWaitingForCustomer(true);
      setFormState({ key: 'customerModal', value: true });
      return;
    }
    setHistoryLoading(true);
    apollo
      .query({
        query: historygql,
        variables: {
          item: item.id,
          salesMan: formState.info.salesMan.id,
          customer: formState.info.customer.value.id,
        },
      })
      .then((data) => {
        if (data.data.salesOrderDetails.length) {
          setQty(data.data.salesOrderDetails[0].quantity);
          setPrice(data.data.salesOrderDetails[0].salesPrice);
          toast.success(
            <b>
              <span>
                History found.
                <br />
                price:{' '}
                <CurrencyFormat
                  value={data.data.salesOrderDetails[0].salesPrice}
                  displayType='text'
                  prefix='$'
                  decimalScale={2}
                  fixedDecimalScale={2}
                />
                <br />
                quantity: {data.data.salesOrderDetails[0].quantity}
                <br />
                Sales Date:{' '}
                <DateFormat value={data.data.salesOrderDetails[0].docDate} />
              </span>
            </b>
          );
        } else {
          toast.warning('No history found.');
        }
      })
      .catch((err) => {
        toast.error(err.message);
      })
      .finally((f) => setHistoryLoading(false));
  };

  const findDetail = () => {
    return formState.details.find((x) => !x.extra && x.item.id == item.id);
  };

  // effects
  useEffect(() => {
    const detail = findDetail();

    if (detail) {
      setDetail(detail);
      setPrice(detail.price);
    } else {
      setPrice(item.suggestedSalesPrice || 0);
    }
  }, []);

  useEffect(() => {
    if (detail && qty == 0) {
      removeDetail(detail);
      setDetail(null);
    } else if (detail) {
      bulkAdjustDetails(detail, { qty, price });
    } else if (qty != 0) {
      const d = addDetail({
        item,
        price,
        qty,
      });
      setDetail(d);
    }
  }, [qty, price]);

  useEffect(() => {
    const cartItem = findDetail();
    if (cartItem) {
      setPrice(cartItem.price);
      setQty(cartItem.qty);
    } else {
      setQty(0);
    }
  }, [formState.details]);

  useEffect(() => {
    if (formState.info.customer && waitingForCustomer) {
      fetchHistory();
      setWaitingForCustomer(false);
    }
  }, [formState.info.customer]);

  useEffect(() => {
    const detail = findDetail();
    if (!detail) {
      const pld = formState.info.priceList?.details.find(
        (pl) => pl.item.id == item.id
      );
      if (pld) setPrice(pld.suggestedSalesPrice);
      else setPrice(item.suggestedSalesPrice);
    }
  }, [item, formState.info.priceList]);

  return (
    <React.Fragment>
      <Form onSubmit={add}>
        <Row className='mt-5'>
          <Col
            xs={3}
            className='d-flex justify-content-center align-items-center'
          >
            <Dash
              size={30}
              className='border rounded-circle p-1 cursor-pointer'
              onClick={() => setPrice(Math.max(0, parseFloat(price) - 0.5))}
            />
          </Col>
          <Col xs={6} className='p-0'>
            <CurrencyFormat
              value={price}
              prefix='$'
              inputMode='decimal'
              decimalScale={2}
              allowNegative={false}
              onValueChange={(values) => setPrice(values.value || 0)}
              className='form-control border-0 border-bottom text-center'
              style={{
                backgroundColor:
                  parseFloat(price) < item.minSalesPrice ? 'lightsalmon' : '',
              }}
            />
          </Col>
          <Col
            xs={3}
            className='d-flex justify-content-center align-items-center'
          >
            <Plus
              size={30}
              className='border rounded-circle p-1 cursor-pointer'
              onClick={() => setPrice(parseFloat(price) + 0.5)}
            />
          </Col>
          <Col
            xs={3}
            className='mt-2 d-flex justify-content-center align-items-center'
          >
            <Dash
              size={30}
              className='border rounded-circle p-1 cursor-pointer'
              onClick={() => setQty(Math.max(0, parseFloat(qty) - 1))}
            />
          </Col>
          <Col xs={6} className='mt-2 p-0'>
            <CurrencyFormat
              value={qty}
              inputMode='decimal'
              decimalScale={2}
              allowNegative={false}
              onValueChange={(values) => setQty(values.value || 0)}
              className='form-control border-0 border-bottom text-center'
              style={{
                backgroundColor:
                  parseFloat(qty) >
                  Math.max(
                    0,
                    (formState.levelHistory &&
                      formState.levelHistory[item.id]) ||
                      0
                  )
                    ? 'lightsalmon'
                    : '',
              }}
            />
          </Col>
          <Col
            xs={3}
            className='mt-2 d-flex justify-content-center align-items-center'
          >
            <Plus
              size={30}
              className='border rounded-circle p-1 cursor-pointer'
              onClick={() => setQty(parseFloat(qty) + 1)}
            />
          </Col>
        </Row>
        <Row className='mt-5'>
          <Col xs={3}>
            <Button className='w-100 border-0' onClick={fetchHistory}>
              {!historyLoading && <ClockHistory size={16} color="white"/>}
              {historyLoading && <Spinner className='spinner-border-sm' size={16} color="white"/>}
            </Button>
          </Col>
          <Col
            xs={6}
            className={
              'd-flex align-items-center justify-content-center' +
              (qty > 0 ? ' rounded text-white bg-success' : '')
            }
          >
            <h4 className='m-0'>
              <DefaultCurrency value={price * qty} />
            </h4>
          </Col>
          {appState.auth.user.entity.config.traceability &&
            appState.auth.user.entity.config.inventory && (
              <Col xs={3}>
                <ItemBatchSummary item={item} />
              </Col>
            )}
        </Row>
      </Form>
    </React.Fragment>
  );
};

export default NormalView;
